import { Grid, Typography } from '@mui/material';
import { useId, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptyTestatorMember,
  getEmptyTestatorMemberData,
  IMemberTestator,
  IMemberTestatorData,
  TestatorMemberDataSchema,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberTestament';
import { CustomeSlide } from '../../../../core/CustomeSlide';
import { useDialogContext } from '../../../../core/DialogPresenter/DialogContext';
import { useRegisterDialogPage } from '../../../../core/DialogPresenter/useRegisterDialogPage';
import { EditLegalClient } from '../../../../LegalClient/EditLegalClient';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  isMainTestatorMember,
  updateOrCreateMember,
  updateOrCreateRelationship,
} from '../../generic/utilities';
import { getOrCreateTestamentRelationship } from '../../Inherit/utilities';
import { TestatorMemberInformation } from '../components/TestamentTestatorInformation';
import { zodResolver } from '@hookform/resolvers/zod';

export type ITestamentMainTestatorProps = {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
};

export function TestamentMainTestator({
  relationshipArray,
  setRelationshipArray,
}: ITestamentMainTestatorProps) {
  const id = useId();
  const { handleNext, handleBack, isNextDisabled } = useDialogContext();

  const pageNumber = useRegisterDialogPage(id, {
    title: 'Erblasser',
  });

  const [legalClientArray, putLegalClient, getLegalClient] =
    useLegalClientStore(state => [
      state.legalClientArray,
      state.putLegalClient,
      state.getLegalClient,
    ]);

  const inheritanceRelationship = useMemo(
    () => getOrCreateTestamentRelationship(relationshipArray),
    [relationshipArray]
  );

  const mainTestator = useMemo(
    () =>
      inheritanceRelationship.Members.find(isMainTestatorMember) ||
      getEmptyTestatorMember(
        {
          ...getEmptyLegalClient(legalClientArray),
          ...getEmptyNaturalPerson(),
        },
        inheritanceRelationship.idRelationship
      ),
    [inheritanceRelationship, legalClientArray]
  );

  const legalClient = getLegalClient(mainTestator.idLegalClient);

  const legalClientMethods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: (legalClient as IEditableLegalClient) || {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const memberDataMethods = useForm<IMemberTestatorData>({
    mode: 'onChange',
    defaultValues:
      mainTestator.data || getEmptyTestatorMemberData('mainTestator'),
    resolver: zodResolver(TestatorMemberDataSchema),
  });

  async function onSubmit() {
    const legalClientValid = legalClientMethods.trigger();
    const memberDataValid = memberDataMethods.trigger();

    const results = await Promise.all([legalClientValid, memberDataValid]);

    const allValid = results.every(valid => valid);
    if (allValid) {
      const formLegalClient = legalClientMethods.getValues();
      const updatedLegalClient = putLegalClient(
        formLegalClient as ILegalClient
      );

      const updatedMemberData = memberDataMethods.getValues();
      const updatedTestator: IMemberTestator = {
        ...mainTestator,
        idLegalClient: updatedLegalClient.idLegalClient,
        data: updatedMemberData,
      };

      const updatedRelationship: ILegalTransactionRelationship = {
        ...inheritanceRelationship,
        Members: updateOrCreateMember(
          inheritanceRelationship.Members,
          updatedTestator
        ),
      };
      setRelationshipArray(prev =>
        updateOrCreateRelationship(prev, updatedRelationship)
      );

      handleNext();
    }
  }

  return pageNumber === null ? null : (
    <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h4' sx={{ mb: 2 }}>
            Primärer Erblasser
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormProvider {...legalClientMethods}>
            <form id='testator-legal-client-form' noValidate>
              <EditLegalClient disableCompanyLegalClient />
            </form>
          </FormProvider>
        </Grid>

        <Grid item xs={12}>
          <FormProvider {...memberDataMethods}>
            <form id='testator-member-data-form' noValidate>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Informationen zur Testamentsausstellung
              </Typography>
              <TestatorMemberInformation />
            </form>
          </FormProvider>
        </Grid>

        <Grid item xs={12}>
          <NavRow
            handleNext={onSubmit}
            handleBack={handleBack}
            disableNext={isNextDisabled()}
          />
        </Grid>
      </Grid>
    </CustomeSlide>
  );
}
